import { useQueryClient } from '@tanstack/react-query';
import { TokenAccount, AiTierLevel } from 'types/AiSubscription';
import { SubscriptionStatus } from '@novaheal/types';
import { useState } from 'react';

type UseOptimisticTokenUpdateReturn = {
  optimisticallyConsumeTokens: (amount?: number) => void;
  revertOptimisticUpdate: () => void;
  isOptimisticUpdate: boolean;
};

export const useOptimisticTokenUpdate = (
  tokenAccount: TokenAccount | undefined,
  userSubscriptionStatus?: SubscriptionStatus
): UseOptimisticTokenUpdateReturn => {
  const queryClient = useQueryClient();
  const [isOptimisticUpdate, setIsOptimisticUpdate] = useState(false);

  const optimisticallyConsumeTokens = (amount = 1): void => {
    if (
      !tokenAccount ||
      tokenAccount.currentTier === AiTierLevel.UNLIMITED ||
      userSubscriptionStatus === SubscriptionStatus.VOUCHER
    ) {
      return;
    }

    queryClient.setQueryData(['token-account'], (oldData: TokenAccount | undefined) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        availableMonthlyTokens: Math.max(0, oldData.availableMonthlyTokens - amount),
        availableDailyTokens: Math.max(0, oldData.availableDailyTokens - amount),
      };
    });
    setIsOptimisticUpdate(true);
  };

  const revertOptimisticUpdate = (): void => {
    // Invalidate the query to force a refetch and get the real server state
    queryClient.invalidateQueries({ queryKey: ['token-account'] });
    setIsOptimisticUpdate(false);
  };

  return {
    optimisticallyConsumeTokens,
    revertOptimisticUpdate,
    isOptimisticUpdate,
  };
};
